import { FetchPurchaseOrderRequest, PurchaseOrder } from "./connection";

export type LabelPrintBatchFileType = "LabelsWithPackingSlips" | "Label" | "PackingSlip" | "Manifest";

// Shipping Documents Dialog Types
export type ShippingDocumentsTabType = "shipping-label" | "packing-slip" | "combined" | "manifest";

export type GroupOption = "product" | "grind" | "quantity";

export interface ShippingDocumentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseOrders?: PurchaseOrder[];
  manifestPdfUrl?: string;
  title?: string;
  initialTab?: ShippingDocumentsTabType;
}

// PDF Cache Types
export interface PdfCacheOptions {
  maxAge?: number; // in milliseconds
  maxSize?: number; // maximum number of cached PDFs
}

export interface CachedPdf {
  url: string;
  timestamp: number;
  accessCount: number;
  lastAccessed: number;
  blob?: Blob;
}

export interface CacheStats {
  total: number;
  valid: number;
  expired: number;
  totalAccessCount: number;
  averageAccessCount: number;
}

export interface LabelPrintBatch {
  type: "labelPrintBatch";
  vendorKey: string;
  batchId: string;
  batchPrintStatus?: BatchPrintStatus;
  batchNotes?: string;
  batchStatus: LabelPrintBatchStatus;
  orderTotal: number;
  totalWeightInGrams?: number;
  totalUnits: number; // line item count
  // -- counts --

  originalOrderCount: number; // order count
  totalOrders: number; // order count. this will be replaced by totalOrderCount
  totalOrderCount?: number;
  labelCreatedOrders: number; // how many got label created. This will be replaced by labelCreatedOrderCount
  labelCreatedOrderCount?: number; // how many got label created
  failedForRetryOrders: number; // how many are being retried. this will be replaced by retryingOrderCount
  retryingOrderCount?: number; // how many are currently being retried

  // -- end of counts --
  totalLabelPrintAttempts: number;
  createdAt?: string;
  updatedAt?: string;
  expirationTime?: number;
  webhookUrl?: string;
  source?: "byobl" | "bcc";
  customerAccountNumber?: string;
  detachedPurchaseOrderIds?: string[];
  // ByoBL webhook grouping. Set on every batch of a single ByoBL request when a
  // webhookUrl is supplied: webhookGroupId links the sibling batches and
  // webhookGroupBatchIds lists all of them, so the group's webhook fires once,
  // after every batch reaches a terminal status. webhookFired is the
  // fire-once guard flag, claimed on the group's leader batch.
  webhookGroupId?: string;
  webhookGroupBatchIds?: string[];
  webhookFired?: boolean;
  manifestObjectKey?: string;
}

export type BatchPrintStatus = BatchPrintPreference | "Processing";

export interface LabelPrintBatchDetail extends LabelPrintBatch {
  purchaseOrders: PurchaseOrder[];
}

export interface LastLabelPrintBatch {
  vendorKey: string;
  batchId: string;
}

export interface LabelPrintBatchQueryResult {
  labelPrintBatches: LabelPrintBatch[];
  lastItem?: LastLabelPrintBatch;
}

export interface LabelPrintBatchFetchResult {
  data: LabelPrintBatchDetail;
  timestamp: string;
  path: string;
}

export interface LabelPrintBatchesFetchRequest {
  params: {
    limit?: number;
    max?: number;
    batchStatus?: string;
    lastBatchId?: string;
  };
}
export interface FetchLabelPrintBatchRequest {
  vendorKey: string;
  batchId: string;
}

export interface LabelPrintBatchesFetchResult {
  data: {
    batchSummary: LabelPrintBatch[];
    lastBatchId?: string;
  };
  timestamp: string;
  path: string;
}

export interface LabelPrintBatchCreateRequest {
  orderIds?: string[];
  batchNotes?: string;
  batchId?: string;
}

export interface GroupBatchRequest {
  orderIds: string[];
  batchNotes?: string;
}

export interface LabelPrintBatchCreateResult {
  data: LabelPrintBatch;
  timestamp: string;
  path: string;
}

export interface LabelPrintBatchCreateError extends LabelPrintBatchCreateResult {
  reasonPhrase?: string;
  message?: string;
  detail?: string;
  errorCode?: string;
}

export interface LabelPrintBatchSummaryRequest {
  vendorKey: string;
  limit: number;
  max?: number;
  batchId?: string;
  createdFrom?: string;
  createdTo?: string;
  batchStatus?: LabelPrintBatchStatus;
  batchNotes?: string;
  lastBatchId?: string;
}

export interface LabelPrintBatchStats {
  batchId: string;
  batchStatus: LabelPrintBatchStatus;
  vendorKey: string;
  totalOrderCount: number;
  labelCreatedOrderCount: number;
  retryingOrderCount: number;
}

export interface BatchLabelPrintSQSMessage {
  vendorKey: string;
  batchId: string;
  serializedVendorDetails: string;
  labelPrintSqsRetryLimit: number;
  purchaseOrderIds?: string[];
}

export interface LabelPrintBatchStatusUpdateMessage {
  vendorKey: string;
  batchId: string;
  serializedVendorDetails: string;
  totalLabelPrintAttempts: number;
  labelPrintSqsRetryLimit: number;
  lastExecutionResult?: {
    zenkraftAddressErrorOrderInfoList?: BatchStatusOrderInfo[];
    labelPrintSucceededOrderInfoList?: BatchStatusOrderInfo[];
    productErrorOrderInfoList?: BatchStatusOrderInfo[];
    labelCreateErrorOrderInfoList?: BatchStatusOrderInfo[];
    genericErrorOrderInfoList?: BatchStatusOrderInfo[];
  };
  lastExecutionResultObjectKey?: string;
}

export interface BatchStatusOrderInfo {
  purchaseOrderId: string;
  message: string;
}

export interface BatchOrderErrorDetails {
  purchaseOrderId: string;
  errorType: LabelPrintErrorType;
  errorMessage: string;
}

export type LabelPrintErrorType = "LabelCreateError" | "ZenkraftAddressError" | "ProductError" | "Error";

export interface LabelPrintRequestMessage {
  vendorKey: string;
  purchaseOrderId: string;
  batchId: string;
  serializedVendorDetails: string;
  totalLabelPrintAttempts: number;
}

export interface BatchLabelPrintSfnMessage {
  vendorKey: string;
  batchId: string;
  serializedVendorDetails: string;
  totalLabelPrintAttempts: number;
  labelPrintSqsRetryLimit: number;
  purchaseOrderIds?: string[];
  requestObjectKey?: string;
}

export const LabelPrintStatusKeys = ["Processing", "Completed", "Failed-for-Retry", "Failed"] as const;

export type LabelPrintBatchStatus = (typeof LabelPrintStatusKeys)[number];

export type BatchPrintPreference = "LabelsOnly" | "LabelsWithPackingSlips";

export interface FetchPurchaseOrderShippingDocumentRequest extends FetchPurchaseOrderRequest {
  batchId?: string;
  type: LabelPrintBatchFileType;
}

export const labelExportUrlEncryptionKeyParamName = "beanz-connect-export-label-url-encryption-key";
/*
{
  "activeKeyId": "v3",
  "keys": {
    "v2": { "value": "...", "activatedAt": "2025-06-01T00:00:00Z" },
    "v1": { "material": "...", "activatedAt": "2025-03-01T00:00:00Z" }
  }
}
*/
export interface LabelExportUrlEncryptionKeys {
  activeKeyId: string;
  keys: {
    [key: string]: {
      value: string; // base64 encoded key
      activatedAt: string; // ISO date string
    };
  };
}
